import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  Cell,
} from 'recharts';
import { motion, useAnimation, useInView } from 'framer-motion';
import gsap from 'gsap';

export interface ChartDataPoint {
  name: string;
  value: number;
  date?: Date;
  target?: number;
  [key: string]: any;
}

interface AnimatedChartProps {
  data: ChartDataPoint[];
  title: string;
  subtitle?: string;
  type?: 'line' | 'bar' | 'area';
  height?: number;
  color?: string;
  showTarget?: boolean;
  targetValue?: number;
  unit?: string;
  isPositive?: boolean;
  enableAnimation?: boolean;
  delay?: number;
  onDataPointClick?: (point: ChartDataPoint) => void;
  compareData?: ChartDataPoint[];
  compareLabel?: string;
}

export const AnimatedChart: React.FC<AnimatedChartProps> = ({
  data,
  title,
  subtitle,
  type = 'line',
  height = 300,
  color,
  showTarget = false,
  targetValue,
  unit = '',
  isPositive = true,
  enableAnimation = true,
  delay = 0,
  onDataPointClick,
  compareData,
  compareLabel = 'Previous',
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const inView = useInView(chartRef, { once: true });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(!enableAnimation);

  const primaryColor = color || theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;

  // Calculate improvement
  const firstValue = data[0]?.value || 0;
  const lastValue = data[data.length - 1]?.value || 0;
  const percentChange = firstValue !== 0
    ? ((lastValue - firstValue) / Math.abs(firstValue)) * 100
    : 0;
  
  const isImprovement = isPositive 
    ? lastValue > firstValue
    : lastValue < firstValue;

  const trendColor = isImprovement 
    ? theme.palette.success.main 
    : percentChange === 0 
    ? theme.palette.warning.main 
    : theme.palette.error.main;

  useEffect(() => {
    if (inView) {
      controls.start('visible');
      // Simplified: just fade in, no complex GSAP animations
      setTimeout(() => setAnimationComplete(true), 300);
    }
  }, [inView, controls]);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: Math.min(delay, 0.1),
      },
    },
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderChart = () => {
    const chartData = showComparison && compareData 
      ? data.map((point, index) => ({
          ...point,
          compareValue: compareData[index]?.value,
        }))
      : data;

    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    };

    const customTooltip = ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        return (
          <Paper
            sx={{
              p: 1.5,
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {label}
            </Typography>
            {payload.map((entry: any, index: number) => (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: entry.color }}
              >
                {entry.name}: {entry.value}{unit}
              </Typography>
            ))}
          </Paper>
        );
      }
      return null;
    };

    switch (type) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              stroke={theme.palette.text.secondary}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              stroke={theme.palette.text.secondary}
            />
            <RechartsTooltip content={customTooltip} />
            {showTarget && targetValue !== undefined && (
              <ReferenceLine 
                y={targetValue} 
                stroke={theme.palette.warning.main}
                strokeDasharray="5 5"
                label="Target"
              />
            )}
            <Bar 
              dataKey="value" 
              fill={primaryColor}
              onClick={onDataPointClick}
              cursor={onDataPointClick ? 'pointer' : 'default'}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={index === data.length - 1 ? trendColor : primaryColor} 
                />
              ))}
            </Bar>
            {showComparison && compareData && (
              <Bar 
                dataKey="compareValue" 
                fill={alpha(secondaryColor, 0.5)}
                name={compareLabel}
              />
            )}
          </BarChart>
        );
      
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={primaryColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={primaryColor} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              stroke={theme.palette.text.secondary}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              stroke={theme.palette.text.secondary}
            />
            <RechartsTooltip content={customTooltip} />
            {showTarget && targetValue !== undefined && (
              <ReferenceLine 
                y={targetValue} 
                stroke={theme.palette.warning.main}
                strokeDasharray="5 5"
                label="Target"
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke={primaryColor}
              fillOpacity={1}
              fill="url(#colorGradient)"
              strokeWidth={2}
              dot={{ fill: primaryColor, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            {showComparison && compareData && (
              <Area
                type="monotone"
                dataKey="compareValue"
                stroke={secondaryColor}
                fillOpacity={0.3}
                fill={secondaryColor}
                strokeWidth={2}
                name={compareLabel}
              />
            )}
          </AreaChart>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              stroke={theme.palette.text.secondary}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              stroke={theme.palette.text.secondary}
            />
            <RechartsTooltip content={customTooltip} />
            {showTarget && targetValue !== undefined && (
              <ReferenceLine 
                y={targetValue} 
                stroke={theme.palette.warning.main}
                strokeDasharray="5 5"
                label="Target"
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={primaryColor}
              strokeWidth={3}
              dot={{ fill: primaryColor, strokeWidth: 2, r: 5 }}
              activeDot={{ r: 8 }}
            />
            {showComparison && compareData && (
              <Line
                type="monotone"
                dataKey="compareValue"
                stroke={secondaryColor}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: secondaryColor, r: 3 }}
                name={compareLabel}
              />
            )}
          </LineChart>
        );
    }
  };

  return (
    <motion.div
      ref={chartRef}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
      style={{ height: '100%' }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {animationComplete && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Chip
                  label={`${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%`}
                  size="small"
                  sx={{
                    mt: 1,
                    backgroundColor: alpha(trendColor, 0.1),
                    color: trendColor,
                    fontWeight: 600,
                  }}
                />
              </motion.div>
            )}
          </Box>
          
          <Box>
            {compareData && (
              <Tooltip title="Toggle comparison">
                <IconButton
                  size="small"
                  onClick={() => setShowComparison(!showComparison)}
                  sx={{ 
                    color: showComparison ? primaryColor : 'text.secondary',
                    mr: 1,
                  }}
                >
                  <CompareIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ width: '100%', height: height }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuClose}>
            <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
            Download as Image
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <FullscreenIcon sx={{ mr: 1, fontSize: 20 }} />
            View Fullscreen
          </MenuItem>
        </Menu>
      </Paper>
    </motion.div>
  );
};

export default AnimatedChart;